import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons'

import type { Table } from '@tanstack/react-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'primitives/base/select'
import { Button } from 'primitives/button'
import type { OffsetOptions } from './types'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  pageSizeOptions?: number[]
  offsetOptions?: OffsetOptions
}

export const DataTablePagination = <TData,>({
  table,
  pageSizeOptions = [10, 20, 30, 40, 50],
  offsetOptions = { isOffsetMode: false, hasNext: false, hasPrevious: false },
}: DataTablePaginationProps<TData>) => {
  const { isOffsetMode, hasNext, hasPrevious } = offsetOptions
  const filteredRowModel = table.getFilteredRowModel()
  const rowCount = table.options.manualPagination
    ? table.getRowCount()
    : filteredRowModel?.rows.length
  // `table.getRowCount()` falls back to the current page's row length in manual mode when the
  // real total is unknown (offset "of many"), which makes a partial page look like a complete
  // total. Derive "do we actually know the total" from the raw rowCount option instead.
  const hasKnownTotal =
    table.options.rowCount !== undefined
      ? table.options.rowCount > 0
      : !table.options.manualPagination && typeof rowCount === 'number' && rowCount > 0
  const noOfPages = hasKnownTotal ? Math.ceil(rowCount / table.getState().pagination.pageSize) : 0
  const isLastPage = hasKnownTotal
    ? table.getState().pagination.pageIndex === noOfPages - 1
    : !hasNext
  // Without a known total the last page is unknowable, so never show jump-to-last in offset
  // "of many" mode — next/prev still page through via hasNext/hasPrevious.
  const showLastPageButton = !isOffsetMode || hasKnownTotal
  return (
    <div className="flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto px-2 py-1 sm:flex-row sm:gap-8">
      <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8 justify-between flex-grow">
        <div className="items-center space-x-2 hidden">
          <p className="whitespace-nowrap text-s-400">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value))
            }}
          >
            <SelectTrigger className="h-8 w-[4.5rem] font-jetbrains text-xs-400 tabular-nums">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={`${pageSize}`}
                  className="font-jetbrains text-xs-400 tabular-nums"
                >
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal text-secondary ml-auto">
          {isOffsetMode ? (
            // For offset mode, show total count if available, otherwise "many"
            <>
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize +
                table.getRowModel().rows.length}{' '}
              {rowCount === 0
                ? ''
                : table.options.rowCount !== undefined
                  ? `of ${rowCount}`
                  : table.getRowModel().rows.length < table.getState().pagination.pageSize &&
                      !hasNext
                    ? ''
                    : 'of many'}{' '}
            </>
          ) : (
            // For traditional mode, show exact count
            <>
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                rowCount,
              )}{' '}
              of {rowCount}
            </>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            aria-label="Go to first page"
            variant="outline"
            className="hidden size-6 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={isOffsetMode ? !hasPrevious : !table.getCanPreviousPage()}
          >
            <DoubleArrowLeftIcon className="size-3.5" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            className="size-6"
            onClick={() => table.previousPage()}
            disabled={isOffsetMode ? !hasPrevious : !table.getCanPreviousPage()}
          >
            <ChevronLeftIcon className="size-3.5" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            className="size-6"
            onClick={() => table.nextPage()}
            disabled={isOffsetMode ? !hasNext : isLastPage}
          >
            <ChevronRightIcon className="size-3.5" aria-hidden="true" />
          </Button>
          {showLastPageButton && (
            <Button
              aria-label="Go to last page"
              variant="outline"
              size="icon"
              className="hidden size-6 lg:flex"
              onClick={() => {
                if (!hasKnownTotal || rowCount === 0) return
                table.setPageIndex(noOfPages - 1)
              }}
              disabled={isOffsetMode ? !hasKnownTotal || rowCount === 0 || isLastPage : isLastPage}
            >
              <DoubleArrowRightIcon className="size-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
